import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useGridQueryParams } from '../hooks/useGridQueryParams'
import { api, type ExportColumnGroup, type ExportRequest, type GridQuery } from '../api/client'
import './ExportScreen.css'

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

/** Where a person's column choice lives. Per browser, deliberately — see below. */
const STORED_COLUMNS = 'prarambh.export.columns'

type Scope = 1 | 2 | 3

/**
 * Export the register to Excel.
 *
 * A PAGE, not an overlay — this codebase routes full pages and keeps exactly two
 * modals, and choosing forty columns is not a transient gesture. Being a route
 * also means an export somebody has set up is a URL: it survives Back, it can be
 * bookmarked, and it can be sent to a colleague.
 *
 * **The filter arrives in the query string**, in exactly the shape the register
 * uses, so `useGridQueryParams` reconstructs it here with no second parser. The
 * ticked rows arrive as `?ids=`, the same way `/report` takes them.
 *
 * **The column list comes from the server.** It is the same registry the workbook
 * writer emits from, so the page can never offer a column that arrives empty. A
 * list hardcoded here is exactly how that happens.
 *
 * **Share is feature-detected, not user-agent sniffed.** `navigator.canShare`
 * with a file is the only thing that actually predicts whether the sheet will
 * open, and on desktop it is absent — so the button is not rendered rather than
 * rendered and broken. There is deliberately no `wa.me` fallback: that carries
 * text only, so it would send a link into an app the recipient cannot sign in to.
 */
export function ExportScreen() {
  const navigate = useNavigate()
  const [params] = useSearchParams()

  // The same parser the register uses, over the same parameter names — so the
  // filter cannot be read one way there and another way here.
  const [query] = useGridQueryParams()

  /*
      The ticked rows, carried in the URL like /report?ids=.

      In the URL rather than in React state because this is now a separate page:
      the selection lives in a hook scoped to the register, and reading it across
      a navigation would give an empty set.
  */
  const selected = useMemo(
    () =>
      (params.get('ids') ?? '')
        .split(',')
        .map(Number)
        .filter((n) => Number.isFinite(n) && n > 0),
    [params],
  )

  const onClose = () => navigate(-1)

  const columns = useQuery({
    queryKey: ['export-columns'],
    queryFn: () => api.getExportColumns(),
    staleTime: Infinity,
  })

  const [scope, setScope] = useState<Scope>(selected.length > 0 ? 3 : 1)
  const [chosen, setChosen] = useState<Set<string> | null>(null)
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set())
  const [emailing, setEmailing] = useState(false)
  const [address, setAddress] = useState('')
  const [note, setNote] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const allKeys = useMemo(
    () => (columns.data ?? []).flatMap((g) => g.columns?.map((c) => c.key ?? '') ?? []),
    [columns.data],
  )

  /*
    Seeded once the registry arrives: everything ticked, unless this browser
    remembers a previous choice.

    Everything, so an export nobody configured is COMPLETE. The failure that
    matters is a client receiving a file that quietly omits half the data, not
    one carrying a column they did not need.
  */
  useEffect(() => {
    if (chosen !== null || allKeys.length === 0) return

    try {
      const stored = localStorage.getItem(STORED_COLUMNS)
      if (stored) {
        const keys = JSON.parse(stored) as string[]
        // Intersected with what the server currently offers, so a preference
        // saved before a column was renamed cannot resurrect a dead key.
        const live = keys.filter((k) => allKeys.includes(k))
        if (live.length > 0) {
          setChosen(new Set(live))
          return
        }
      }
    } catch {
      // A corrupt or unreadable preference is not worth failing over; the
      // default is the safe one anyway.
    }

    setChosen(new Set(allKeys))
  }, [allKeys, chosen])

  const chosenKeys = useMemo(() => [...(chosen ?? [])], [chosen])

  const request: ExportRequest = useMemo(
    () => ({
      scope,
      // Empty means "everything" on the server too, so the two agree.
      columns: chosenKeys.length === allKeys.length ? null : chosenKeys,
      vehicleIds: scope === 3 ? selected : null,
      filter: scope === 2 ? (query as ExportRequest['filter']) : null,
    }),
    [scope, chosenKeys, allKeys.length, selected, query],
  )

  // What this would produce, so nobody generates a file to discover it is empty.
  const count = useQuery({
    queryKey: ['export-count', JSON.stringify(request)],
    queryFn: () => api.countExport(request),
    enabled: chosen !== null,
  })

  function remember(next: Set<string>) {
    setChosen(next)
    try {
      localStorage.setItem(STORED_COLUMNS, JSON.stringify([...next]))
    } catch {
      // Private browsing, or storage disabled. The choice still applies to this
      // export; it simply will not be remembered.
    }
  }

  function toggleColumn(key: string) {
    const next = new Set(chosen ?? [])
    if (next.has(key)) next.delete(key)
    else next.add(key)
    remember(next)
  }

  function toggleGroup(group: ExportColumnGroup) {
    const keys = group.columns?.map((c) => c.key ?? '') ?? []
    const next = new Set(chosen ?? [])
    const allOn = keys.every((k) => next.has(k))
    for (const k of keys) {
      if (allOn) next.delete(k)
      else next.add(k)
    }
    remember(next)
  }

  const download = useMutation({
    mutationFn: () => api.exportWorkbook(request),
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName()
      a.click()
      // Or the blob is held for the life of the page.
      URL.revokeObjectURL(url)
      setNote(`${count.data?.rows ?? 0} vehicles downloaded.`)
    },
    onError: (e: Error) => setError(e.message),
  })

  const share = useMutation({
    mutationFn: async () => {
      const blob = await api.exportWorkbook(request)
      const file = new File([blob], fileName(), { type: XLSX_MIME })
      await navigator.share({
        files: [file],
        title: 'Fleet export',
        text: `${count.data?.rows ?? 0} vehicles from Prarambh.`,
      })
    },
    onError: (e: Error) => {
      // Dismissing the share sheet rejects with AbortError. That is a choice,
      // not a failure, and showing an error for it would be wrong.
      if (e.name !== 'AbortError') setError(e.message)
    },
  })

  const email = useMutation({
    mutationFn: () => api.emailExport(address.trim(), request),
    onSuccess: (r) => {
      setEmailing(false)
      setAddress('')
      setNote(`Sent to ${r.rows} vehicles' worth of data — delivery #${r.emailDeliveryId}.`)
    },
    onError: (e: Error) => setError(e.message),
  })

  function fileName() {
    const d = new Date().toISOString().slice(0, 10)
    return `Prarambh-vehicles-${d}.xlsx`
  }

  const canShare =
    typeof navigator !== 'undefined' &&
    typeof navigator.canShare === 'function' &&
    navigator.canShare({ files: [new File([], 'x.xlsx', { type: XLSX_MIME })] })

  const busy = download.isPending || share.isPending || email.isPending
  const nothing = count.data?.rows === 0

  return (
    <div className="expp">
      <header className="expp__head">
        <button type="button" className="expp__back" onClick={onClose}>
          ← Back
        </button>
        <div>
          <p className="eyebrow">Fleet Due Date</p>
          <h1>Export to Excel</h1>
          <p className="expp__sub">
            Choose the vehicles and the columns, then download, email or share it.
          </p>
        </div>
      </header>

      <div className="expp__cols2">

      <section className="expp__band">
        <h3>Which vehicles</h3>

        <label className="expp__scope">
          <input type="radio" checked={scope === 1} onChange={() => setScope(1)} />
          <span>
            All vehicles
            <em>Everything on the register</em>
          </span>
        </label>

        <label className="expp__scope">
          <input type="radio" checked={scope === 2} onChange={() => setScope(2)} />
          <span>
            Current filter
            <em>{describeQuery(query)}</em>
          </span>
        </label>

        <label className={`expp__scope${selected.length === 0 ? ' expp__scope--off' : ''}`}>
          <input
            type="radio"
            checked={scope === 3}
            disabled={selected.length === 0}
            onChange={() => setScope(3)}
          />
          <span>
            Selected rows
            <em>
              {selected.length === 0
                ? 'Nothing ticked in the register'
                : `${selected.length} ticked`}
            </em>
          </span>
        </label>
      </section>

      <section className="expp__band">
        <h3>
          Columns
          <span className="expp__count">
            {chosenKeys.length} of {allKeys.length}
          </span>
        </h3>

        <div className="expp__bulk">
          <button type="button" onClick={() => remember(new Set(allKeys))}>
            All
          </button>
          <button type="button" onClick={() => remember(new Set())}>
            None
          </button>
        </div>

        {columns.isPending && <p className="expp__muted">Loading columns…</p>}

        {(columns.data ?? []).map((group) => {
          const keys = group.columns?.map((c) => c.key ?? '') ?? []
          const on = keys.filter((k) => chosen?.has(k)).length
          const open = openGroups.has(group.name ?? '')

          return (
            <div key={group.name} className="expp__group">
              <div className="expp__grouphead">
                <label>
                  <input
                    type="checkbox"
                    checked={on === keys.length && keys.length > 0}
                    // Partly chosen is its own state: an unticked box on a group
                    // where four of nine are on would read as "none of these".
                    ref={(el) => {
                      if (el) el.indeterminate = on > 0 && on < keys.length
                    }}
                    onChange={() => toggleGroup(group)}
                  />
                  <b>{group.name}</b>
                  <span className="expp__groupcount">
                    {on} of {keys.length}
                  </span>
                </label>

                <button
                  type="button"
                  className="expp__disclose"
                  aria-expanded={open}
                  aria-label={open ? `Hide ${group.name} columns` : `Show ${group.name} columns`}
                  onClick={() =>
                    setOpenGroups((was) => {
                      const next = new Set(was)
                      const key = group.name ?? ''
                      if (next.has(key)) next.delete(key)
                      else next.add(key)
                      return next
                    })
                  }
                >
                  {open ? '▾' : '▸'}
                </button>
              </div>

              <p className="expp__groupdesc">{group.description}</p>

              {open && (
                <div className="expp__cols">
                  {(group.columns ?? []).map((c) => (
                    <label key={c.key} className="expp__col">
                      <input
                        type="checkbox"
                        checked={chosen?.has(c.key ?? '') ?? false}
                        onChange={() => toggleColumn(c.key ?? '')}
                      />
                      {c.header}
                    </label>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </section>

      <section className="expp__band expp__band--out">
        <p className="expp__file">
          <span className="mono">{fileName()}</span>
          <span className="expp__size">
            {count.isPending
              ? '…'
              : `${(count.data?.rows ?? 0).toLocaleString()} × ${chosenKeys.length}`}
          </span>
        </p>

        {/* Said before the button is pressed, not after the file arrives short. */}
        {count.data?.truncated && (
          <p className="expp__warn">
            Only the first {count.data.rows.toLocaleString()} of{' '}
            {count.data.totalRows.toLocaleString()} will be exported. Narrow the filter for
            the rest.
          </p>
        )}

        {nothing && <p className="expp__warn">Nothing matches that — there is no file to make.</p>}

        {chosenKeys.length === 0 && <p className="expp__warn">Choose at least one column.</p>}

        {error && <p className="expp__error">{error}</p>}
        {note && !error && <p className="expp__note">{note}</p>}

        {!emailing ? (
          <div className="expp__actions">
            <button
              type="button"
              className="btn btn--exp"
              disabled={busy || nothing || chosenKeys.length === 0}
              onClick={() => {
                setError(null)
                setNote(null)
                download.mutate()
              }}
            >
              {download.isPending ? 'Preparing…' : 'Download'}
            </button>

            <button
              type="button"
              className="btn btn--exp"
              disabled={busy || nothing || chosenKeys.length === 0}
              onClick={() => {
                setError(null)
                setNote(null)
                setEmailing(true)
              }}
            >
              Email…
            </button>

            {/*
              Only where the browser can actually share a file. Feature-detected
              rather than guessed from the user agent, and deliberately absent on
              desktop instead of present and failing.
            */}
            {canShare && (
              <button
                type="button"
                className="btn btn--exp"
                disabled={busy || nothing || chosenKeys.length === 0}
                onClick={() => {
                  setError(null)
                  setNote(null)
                  share.mutate()
                }}
              >
                {share.isPending ? 'Preparing…' : 'Share'}
              </button>
            )}
          </div>
        ) : (
          <div className="expp__email">
            <label className="expp__field">
              <span>Send to</span>
              <input
                type="email"
                value={address}
                autoFocus
                placeholder="name@example.com"
                onChange={(e) => setAddress(e.target.value)}
              />
            </label>

            <div className="expp__actions">
              <button type="button" className="btn btn--exp" onClick={() => setEmailing(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn--exp btn--expgo"
                disabled={email.isPending || address.trim().length === 0}
                onClick={() => {
                  setError(null)
                  email.mutate()
                }}
              >
                {email.isPending ? 'Sending…' : 'Send'}
              </button>
            </div>
          </div>
        )}
        </section>
      </div>
    </div>
  )
}

/**
 * The current filter, in words.
 *
 * Only enough to tell one filter from another on screen — the authoritative
 * description is generated on the server and written into the workbook's About
 * sheet, so the file cannot describe itself differently from the data in it.
 */
function describeQuery(query: GridQuery): string {
  const parts: string[] = []

  if (query.search) parts.push(`"${query.search}"`)
  if (query.siteId) parts.push('one site')
  if (query.siteIds?.length) parts.push(`${query.siteIds.length} sites`)
  if (query.vehicleTypeId) parts.push('one type')
  if (query.obligationTypeId) parts.push('one obligation')
  if (query.expiryFrom || query.expiryTo) parts.push('an expiry window')
  if (query.worstSeverity != null || query.hasSeverity != null) parts.push('a status')
  if (query.fuelType || query.rcStatus || query.financer || query.emissionNorms)
    parts.push('RTO details')
  if (query.documentFilter != null) parts.push('certificates')

  return parts.length === 0 ? 'No filters are set — this is everything' : `Filtered by ${parts.join(', ')}`
}
